import { reactive, watch } from 'vue';

/**
 * The agent's CallUp call list, shared by the Prospect, Client and Agent lists.
 *
 * It is separate from each page's checkbox selection (which drives bulk delete / move /
 * transfer and resets when you leave the page): checking a row adds it here, unchecking
 * removes it, and it keeps its entries while you move between lists — like the CallUp
 * plugin's list. Kept per browser tab (sessionStorage) so yesterday's picks never reappear.
 *
 * Only { type, id } is stored. Names, phones, Do Not Call and permissions are resolved
 * by the server when the list is previewed or sent.
 */
const STORAGE_KEY = 'crmCallupList';
const TYPES = ['prospect', 'client', 'agent'];

function load() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(saved)
      ? saved.filter((e) => e && TYPES.includes(e.type) && Number.isInteger(e.id))
      : [];
  } catch (e) {
    return [];
  }
}

const state = reactive({ entries: load() });

watch(
  () => JSON.stringify(state.entries),
  (json) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, json);
    } catch (e) {
      /* storage unavailable: the list just won't survive a reload */
    }
  }
);

const indexOf = (type, id) => state.entries.findIndex((e) => e.type === type && e.id === id);

export const callupList = {
  state,

  get count() {
    return state.entries.length;
  },

  has(type, id) {
    return indexOf(type, id) !== -1;
  },

  add(type, id) {
    if (TYPES.includes(type) && Number.isInteger(id) && !this.has(type, id)) {
      state.entries.push({ type, id });
    }
  },

  remove(type, id) {
    const i = indexOf(type, id);
    if (i !== -1) state.entries.splice(i, 1);
  },

  clear() {
    state.entries.splice(0, state.entries.length);
  },

  /** Mirror a page's checkbox selection: newly checked ids are added, unchecked ones removed. */
  applySelection(type, newIds, oldIds) {
    const now = new Set(newIds);
    const before = new Set(oldIds || []);
    now.forEach((id) => { if (!before.has(id)) this.add(type, Number(id)); });
    before.forEach((id) => { if (!now.has(id)) this.remove(type, Number(id)); });
  },
};
